import { FC, ReactNode, useId, useState } from 'react';
import classNames from 'classnames';
import styles from './chartFrame.module.sass';

export interface ChartFrameLegendItem {
  label: string;
  color: string;
}

export interface ChartFrameProps {
  /** Eyebrow (uppercase) above the title — e.g. "CREDIT QUALITY" */
  eyebrow?: string;
  /** Main title — 13.5px / 600 */
  title?: string;
  /** Subtitle — 12.5px / muted */
  subtitle?: string;
  /** Inline KPIs rendered below the title (use <KpiInline />) */
  kpi?: ReactNode;
  /** Optional swatched legend, rendered top-right */
  legend?: ChartFrameLegendItem[];
  /** Slot for right-side controls (toolbar / select / button) */
  headerRight?: ReactNode;
  /** Body content (chart svg, table, anything) */
  children?: ReactNode;
  /** Footer note */
  footer?: ReactNode;
  /** Body padding mode */
  bodyPadding?: 'default' | 'padded' | 'tight';
  /** Extra className for the wrapper */
  className?: string;
  /** Lets the reader fold the body away. The header stays, so `headerRight`
   *  controls keep working while closed. */
  collapsible?: boolean;
  /** Folded on first render. Only read when `collapsible`. */
  defaultCollapsed?: boolean;
  /** Shown next to the fold control while closed ("36 months", "128 loans"),
   *  so the reader knows how much is hidden. */
  collapsedSummary?: string;
}

/**
 * ChartFrame — wrapper for any chart: header (eyebrow, title, subtitle, KPI
 * row, legend, right-side controls), body and footer note.
 */
export const ChartFrame: FC<ChartFrameProps> = ({
  eyebrow,
  title,
  subtitle,
  kpi,
  legend,
  headerRight,
  children,
  footer,
  bodyPadding = 'default',
  className,
  collapsible = false,
  defaultCollapsed = false,
  collapsedSummary,
}) => {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);
  const bodyId = useId();
  const isCollapsed = collapsible && collapsed;

  const foldControl = collapsible ? (
    <button
      type="button"
      className={styles.fold}
      onClick={() => setCollapsed((c) => !c)}
      aria-expanded={!isCollapsed}
      aria-controls={bodyId}
    >
      {isCollapsed && collapsedSummary && (
        <span className={styles.foldSummary}>{collapsedSummary}</span>
      )}
      {isCollapsed ? 'Show' : 'Hide'}
      <span aria-hidden className={classNames(styles.chevron, !isCollapsed && styles.chevronOpen)}>
        ▾
      </span>
    </button>
  ) : null;

  return (
    <section className={classNames('gt-scope', styles.frame, className)}>
      {(eyebrow || title || subtitle || kpi || legend || headerRight || foldControl) && (
        <header className={styles.header}>
          <div className={styles.headLeft}>
            {eyebrow && <span className={styles.eyebrow}>{eyebrow}</span>}
            {title && <span className={styles.title}>{title}</span>}
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            {kpi && <div className={styles.kpiRow}>{kpi}</div>}
          </div>
          {(legend || headerRight || foldControl) && (
            <div className={styles.headRight}>
              {/* The legend folds with the body. */}
              {legend && !isCollapsed && (
                <div className={styles.legend}>
                  {legend.map((it) => (
                    <span key={it.label} className={styles.legendItem}>
                      <span className={styles.swatch} style={{ background: it.color }} />
                      {it.label}
                    </span>
                  ))}
                </div>
              )}
              {headerRight}
              {foldControl}
            </div>
          )}
        </header>
      )}
      {!isCollapsed && (
        <div
          id={bodyId}
          className={classNames(
            styles.body,
            bodyPadding === 'padded' && styles.bodyPadded,
            bodyPadding === 'tight' && styles.bodyTight,
          )}
        >
          {children}
        </div>
      )}
      {footer && !isCollapsed && <div className={styles.footer}>{footer}</div>}
    </section>
  );
};

ChartFrame.displayName = 'ChartFrame';
export default ChartFrame;
